import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Plus } from 'lucide-react'
import Reveal from './Reveal'

const FAQS = [
  {
    q: 'Do I own the website?',
    a: 'Yes. You own the code, the design, and the domain. No lock-in, no platform fees, and no monthly subscription for the site itself.',
  },
  {
    q: 'How long does it take?',
    a: 'Starter sites land in 5 to 7 business days, Pro in 10, and Growth in 14. The clock starts once I have your content and brief.',
  },
  {
    q: 'Can you integrate my booking system?',
    a: 'Yes. I work with Calendly, Acuity, HotDoc, Cliniko, SimplyBook, and most other scheduling tools, or I can link through to your existing system.',
  },
  {
    q: 'What if I need changes after delivery?',
    a: 'Each package includes revision rounds. After delivery, changes are billed at $100/hour, or you can add a monthly maintenance retainer.',
  },
  {
    q: 'What platform do you build on?',
    a: 'Fast, lightweight sites built for performance and local SEO. No WordPress bloat, just clean code, clear page structure, and a strong technical foundation.',
  },
  {
    q: 'How much does a small business website cost in Melbourne?',
    a: 'CrateWeb websites start from $1,500. The Starter package covers up to 5 pages, mobile-friendly design, on-page SEO setup, contact forms, and basic launch support.',
  },
  {
    q: 'Can you help with my Google Business Profile?',
    a: 'Yes. Google Business Profile management starts from $299 per month and can include profile optimisation, posts, service updates, review support, and monthly reporting.',
  },
  {
    q: 'Is SEO included with the website?',
    a: 'Every website includes on-page SEO basics: page titles, meta descriptions, headings, sitemap setup, schema, and clear service copy. Ongoing local SEO can be added on a retainer.',
  },
  {
    q: 'Do you offer website maintenance?',
    a: 'Yes. Website maintenance starts from $199 per month and can include hosting, technical updates, small content changes, uptime monitoring, and reporting.',
  },
  {
    q: 'Can you promise a specific Google ranking?',
    a: 'No. No honest web designer or SEO provider can. I focus on the technical setup, content, profile alignment, and steady improvements that support local search visibility.',
  },
  {
    q: 'How do I get started?',
    a: 'Fill out the contact form or call 0450 692 980. We have a quick 15-minute chat to understand your business, then I send a proposal within 24 hours.',
  },
]

const FAQItem = ({ q, a, isOpen, onToggle }: { q: string; a: string; isOpen: boolean; onToggle: () => void }) => (
  <div className="border-b border-[var(--line)]">
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      className="flex w-full items-center justify-between gap-6 py-6 text-left"
    >
      <span className="font-display text-lg font-bold text-ink sm:text-xl">{q}</span>
      <span
        className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-full border border-[var(--line-strong)] transition-all duration-300 ${
          isOpen ? 'rotate-45 border-gold bg-gold text-white' : 'text-ink'
        }`}
      >
        <Plus className="h-4 w-4" strokeWidth={2.5} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="max-w-2xl pb-6 text-[0.98rem] leading-relaxed text-ink-soft">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)

const FAQ = () => {
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section id="faq" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-container px-5 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
          <Reveal className="lg:sticky lg:top-28 lg:self-start">
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
              Questions, answered straight.
            </h2>
            <p className="mt-6 max-w-sm text-lg text-ink-soft">
              No jargon, no hard sell. If something is not here, call me on
              <a href="tel:0450692980" className="font-semibold text-gold"> 0450 692 980</a>.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <div>
              {FAQS.map((f, i) => (
                <FAQItem key={f.q} q={f.q} a={f.a} isOpen={open === i} onToggle={() => setOpen(open === i ? null : i)} />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

export default FAQ
